import { useEffect, useRef } from 'react'

import { addCrossTabListener, sendCrossTabEvent } from './cross-tab-communication'

export interface FolderDeletionEvent {
  folderId: string
  folderName: string
  parentId?: string
  timestamp: number
}

/**
 * Hook to listen for folder deletion events from other tabs
 * This helps keep folder lists in sync across tabs
 */
export const useFolderDeletionListener = (
  onFolderDeleted: (event: FolderDeletionEvent) => void,
) => {
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // Set up listener for folder deletion events
    cleanupRef.current = addCrossTabListener('FOLDER_DELETED', (data: FolderDeletionEvent) => {
      console.log('📁 Folder deletion detected from another tab:', data)
      onFolderDeleted(data)
    })

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [onFolderDeleted])
}

/**
 * Hook to send folder deletion events to other tabs
 * Call this when a folder is deleted in the current tab
 */
export const useFolderDeletionNotifier = () => {
  const notifyFolderDeleted = (folderId: string, folderName: string, parentId?: string) => {
    const event: FolderDeletionEvent = {
      folderId,
      folderName,
      parentId,
      timestamp: Date.now(),
    }

    console.log('📁 Notifying other tabs about folder deletion:', event)
    sendCrossTabEvent('FOLDER_DELETED', event)
  }

  return { notifyFolderDeleted }
}

/**
 * Combined hook that provides both listening and notifying capabilities
 */
export const useFolderDeletionSync = (onFolderDeleted?: (event: FolderDeletionEvent) => void) => {
  const { notifyFolderDeleted } = useFolderDeletionNotifier()

  useFolderDeletionListener(event => {
    onFolderDeleted?.(event)
  })

  return { notifyFolderDeleted }
}
